import type { Outcome, StoryView } from "./story";

/**
 * Finding your way in a long story (the story rail and map, client/rail.tsx): its chapters, and a search that forgives
 * a half-remembered name. Pure functions over the StoryView the page already has: no requests, no model calls.
 */

// ---- chapters -----------------------------------------------------------------------------------------------------

export interface ChapterInfo {
  /** Chapter number (0: the turns before the first chapter began). */
  k: number;
  /** First and last turn. */
  from: number;
  to: number;
  /** The chapter's dramatic question, and how it ended (null while open, or for turns before the first chapter). */
  question: string | null;
  outcome: Outcome | null;
  /** What an ending at a price or a loss took ("the lamp room floods"), when the canon pass read it. */
  price: string | null;
  /** The chapter the story is in now. */
  current: boolean;
  /** People, places and things that first appeared in it. */
  names: string[];
}

export function chaptersOf(view: Pick<StoryView, "turns" | "chapterStarts" | "chapter" | "things">): ChapterInfo[] {
  const turns = view.turns;
  if (!turns.length) return [];
  const first = turns[0]!.n, last = turns.at(-1)!.n;
  const starts = [...new Set(view.chapterStarts)].filter((s) => s <= last).sort((a, b) => a - b);
  // How each chapter ended, by its number. A chapter ends on turn t, and the next one opens on that same turn's
  // canon pass (so its recorded start is t too): the ending belongs to the chapter that ended, which keeps turn t.
  const ends = new Map<number, { at: number; question: string; outcome: ChapterInfo["outcome"]; price: string | null }>();
  for (const t of turns) if (t.chapterEnd) ends.set(t.chapterEnd.n, { at: t.n, question: t.chapterEnd.question, outcome: t.chapterEnd.outcome, price: t.chapterEnd.price ?? null });
  const spans: { k: number; from: number }[] = [];
  if (!starts.length || starts[0]! > first) spans.push({ k: 0, from: first });
  starts.forEach((from, i) => spans.push({ k: i + 1, from: Math.max(from, first) }));
  for (let i = 1; i < spans.length; i++) {
    const before = ends.get(spans[i - 1]!.k);
    if (before && spans[i]!.from <= before.at) spans[i]!.from = before.at + 1;
  }
  return spans.filter((s) => s.from <= last).map((s, i, all) => {
    const to = i + 1 < all.length ? all[i + 1]!.from - 1 : last;
    const end = ends.get(s.k);
    const isLast = i === all.length - 1;
    const open = isLast && view.chapter && view.chapter.n === s.k && !end ? view.chapter : null;
    return {
      k: s.k, from: s.from, to,
      question: end?.question ?? open?.question ?? null,
      outcome: end?.outcome ?? null,
      price: end?.price ?? null,
      current: isLast,
      names: view.things.filter((t) => t.turn >= s.from && t.turn <= to).sort((a, b) => a.turn - b.turn).map((t) => t.name),
    };
  });
}

/** The chapter a turn belongs to. */
export const chapterAt = (chapters: ChapterInfo[], n: number) => chapters.find((c) => n >= c.from && n <= c.to) ?? null;

// ---- matching -----------------------------------------------------------------------------------------------------

/** Lower case, accents off, curly quotes straightened: "Café Noël’s" -> "cafe noel's". */
export function fold(s: string): string {
  return s.normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[‘’]/g, "'").toLowerCase();
}

/** The words of a text with where each starts and ends in the original (for snippets and highlights). */
export function wordsOf(text: string): { w: string; at: number; end: number }[] {
  const out: { w: string; at: number; end: number }[] = [];
  const re = /[\p{L}\p{N}]+(?:['’][\p{L}]+)*/gu;
  for (let m: RegExpExecArray | null; (m = re.exec(text)); ) out.push({ w: fold(m[0]), at: m.index, end: m.index + m[0].length });
  return out;
}

/** The words of a query, folded; tiny words ("a", "of") dropped unless they are all there is. */
export function termsOf(query: string): string[] {
  const all = wordsOf(query).map((x) => x.w);
  const big = all.filter((w) => w.length > 2 || /\d/.test(w));
  return big.length ? big : all;
}

/** Edit distance (with transpositions), giving up once it exceeds `max`. */
function distance(a: string, b: string, max: number): number {
  if (Math.abs(a.length - b.length) > max) return max + 1;
  let prev2: number[] = [], prev = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    const cur = [i];
    let best = i;
    for (let j = 1; j <= b.length; j++) {
      let d = Math.min(prev[j]! + 1, cur[j - 1]! + 1, prev[j - 1]! + (a[i - 1] === b[j - 1] ? 0 : 1));
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) d = Math.min(d, prev2[j - 2]! + 1);
      cur.push(d);
      best = Math.min(best, d);
    }
    if (best > max) return max + 1;
    prev2 = prev; prev = cur;
  }
  return prev[b.length]!;
}

/** Typos forgiven: none for short words, one from four letters, two from eight. */
const slack = (term: string) => (term.length >= 8 ? 2 : term.length >= 4 ? 1 : 0);

/**
 * How well one word of the story answers one word of the query: 3 exact, 2 the start of the word ("light" in
 * "lighthouse"), 1 close enough to be a misremembering ("Marguerite" for "Margarethe"), 0 no.
 */
export function wordScore(word: string, term: string): 0 | 1 | 2 | 3 {
  if (word === term) return 3;
  if (term.length >= 2 && word.startsWith(term)) return 2;
  const k = slack(term);
  if (!k) return 0;
  if (distance(word, term, k) <= k) return 1;
  // A misremembered start of a long word: "lightous" for "lighthouse".
  if (term.length >= 5 && word.length > term.length && distance(word.slice(0, term.length), term, k) <= k) return 1;
  return 0;
}

/** For each query term, the best score any of the words gets (0 if one term has no match at all). */
function scoreWords(words: string[], terms: string[]): number {
  let total = 0;
  for (const term of terms) {
    let best = 0;
    for (const w of words) { const s = wordScore(w, term); if (s > best) { best = s; if (s === 3) break; } }
    if (!best) return 0;
    total += best;
  }
  return total;
}

// ---- search -------------------------------------------------------------------------------------------------------

export type NameKind = "person" | "place" | "thing" | "thread";
export interface NameHit {
  id: string; kind: NameKind; name: string; hint: string;
  /** The turns it appears in (first is where it entered the story). */
  seen: number[];
  /** Matched by its name (vs. only by what it is: "the woman with the red umbrella"). */
  byName: boolean;
  /** Closeness of the match: higher is better. */
  score: number;
}
/** A piece of a snippet: plain text, or a matched word. */
export interface Piece { text: string; hit: boolean }
export interface TurnHit {
  n: number;
  /** Where the words were found: the player's line or the narrator's. */
  where: "you" | "story";
  snippet: Piece[];
  /** Every query word matched exactly (not by a guess). */
  exact: boolean;
}
export interface FindResult { terms: string[]; names: NameHit[]; turns: TurnHit[]; more: number }

const TURN_LIMIT = 60;
const SNIPPET = 64;

/** The words around the first hit in a text, with the hits marked. */
export function snippetOf(text: string, terms: string[], width = SNIPPET): Piece[] {
  const words = wordsOf(text);
  const hits = words.filter((x) => terms.some((t) => wordScore(x.w, t) > 0));
  if (!hits.length) return [{ text: text.slice(0, width * 2), hit: false }];
  let from = Math.max(0, hits[0]!.at - width), to = Math.min(text.length, hits[0]!.end + width);
  // Cut on word boundaries.
  if (from > 0) { const sp = text.indexOf(" ", from); from = sp >= 0 && sp < hits[0]!.at ? sp + 1 : from; }
  if (to < text.length) { const sp = text.lastIndexOf(" ", to); to = sp > hits[0]!.end ? sp : to; }
  const pieces: Piece[] = [];
  if (from > 0) pieces.push({ text: "…", hit: false });
  let at = from;
  for (const h of hits) {
    if (h.at < from || h.end > to) continue;
    if (h.at > at) pieces.push({ text: text.slice(at, h.at), hit: false });
    pieces.push({ text: text.slice(h.at, h.end), hit: true });
    at = h.end;
  }
  if (at < to) pieces.push({ text: text.slice(at, to), hit: false });
  if (to < text.length) pieces.push({ text: "…", hit: false });
  return pieces;
}

/** The view's words, folded once per view (a long story has tens of thousands). */
const indexCache = new WeakMap<object, { n: number; you: string[]; story: string[] }[]>();
function turnIndex(view: Pick<StoryView, "turns">) {
  let idx = indexCache.get(view.turns);
  if (!idx) {
    idx = view.turns.map((t) => ({ n: t.n, you: wordsOf(t.input).map((x) => x.w), story: wordsOf(t.prose).map((x) => x.w) }));
    indexCache.set(view.turns, idx);
  }
  return idx;
}

/**
 * Searches a story for a half-remembered word or name: names first (people, places, things, threads; by name, then by
 * what they are), then every turn where all the words appear, in story order. Forgives typos and partial words.
 */
export function findIn(view: Pick<StoryView, "turns" | "things" | "threads">, query: string): FindResult {
  const terms = termsOf(query);
  if (!terms.length) return { terms, names: [], turns: [], more: 0 };

  const named = [
    ...view.things.map((t) => ({ id: t.id, kind: t.kind as NameKind, name: t.name, hint: t.hint, seen: t.seen.length ? t.seen : [t.turn] })),
    ...view.threads.map((t) => ({ id: `thread:${t.id}`, kind: "thread" as const, name: t.name, hint: t.hint, seen: t.seen.length ? t.seen : [t.turn] })),
  ];
  const names: NameHit[] = [];
  for (const x of named) {
    const nameWords = wordsOf(x.name).map((w) => w.w);
    const byName = scoreWords(nameWords, terms);
    const byAll = byName || scoreWords([...nameWords, ...wordsOf(x.hint).map((w) => w.w)], terms);
    if (byAll) names.push({ ...x, byName: byName > 0, score: byName ? 100 + byName : byAll });
  }
  names.sort((a, b) => b.score - a.score || a.seen[0]! - b.seen[0]!);

  const turns: TurnHit[] = [];
  let more = 0;
  const full = new Map(view.turns.map((t) => [t.n, t]));
  for (const t of turnIndex(view)) {
    const you = scoreWords(t.you, terms), story = scoreWords(t.story, terms);
    if (!you && !story) continue;
    if (turns.length >= TURN_LIMIT) { more++; continue; }
    const where = story >= you ? "story" : "you";
    const text = where === "story" ? full.get(t.n)!.prose : full.get(t.n)!.input;
    turns.push({ n: t.n, where, snippet: snippetOf(text, terms), exact: Math.max(you, story) === terms.length * 3 });
  }
  return { terms, names, turns, more };
}

/**
 * Does the reply actually bring the thread back? The dream plans a return and the narrator is asked for it, but it
 * can leave it out: the label shows (and the return counts as used) only once the prose names it (a word of the
 * thread's name, 4+ letters).
 */
export function mentions(prose: string, name: string): boolean {
  const said = wordsOf(prose).map((w) => w.w);
  const key = wordsOf(name).map((w) => w.w).filter((w) => w.length >= 4 && !STOP.has(w));
  return key.length > 0 && key.some((k) => said.some((w) => wordScore(w, k) >= 2));
}
const STOP = new Set(["that", "this", "with", "from", "what", "when", "where", "which", "will", "your", "their", "there", "about", "into", "back"]);
